/**
 * Android app only: checks the published release and offers an in-app update.
 * The native side only accepts APKs from this CRM's own HTTPS origin, verifies
 * the SHA-256 published in latest.json, and Android itself rejects any APK
 * not signed with the same key as the installed app.
 */
import { useEffect, useState } from "react";
import { fetchLatestAndroidRelease, isNativeApp, nativeAppInfo } from "./platform";
import "./appDownload.css";

const CHECK_EVERY_MS = 6 * 60 * 60 * 1000;
const SNOOZE_KEY = "travnok-update-snoozed";

export default function NativeUpdateBanner() {
  const [update, setUpdate] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const check = async () => {
      try {
        const installed = nativeAppInfo();
        const r = await fetchLatestAndroidRelease();
        if (!installed || r.versionCode <= installed.versionCode) return setUpdate(null);
        const mandatory = Number(r.minSupportedVersionCode || 0) > installed.versionCode;
        if (!mandatory && localStorage.getItem(SNOOZE_KEY) === String(r.versionCode)) return undefined;
        setUpdate({ ...r, mandatory, installed });
      } catch {
        /* offline or no release: try again later */
      }
      return undefined;
    };
    check();
    const t = setInterval(check, CHECK_EVERY_MS);
    return () => clearInterval(t);
  }, []);

  if (!update) return null;

  const start = () => {
    setBusy(true);
    const result = window.TravnokNative.downloadUpdate(
      window.__TRAVNOK_NATIVE_TOKEN || "",
      new URL(update.url, window.location.origin).href,
      update.sha256
    );
    if (result !== "ok") {
      setBusy(false);
      window.alert(`Update could not start: ${result}`);
    }
  };

  return (
    <div className="app-update" role="status">
      <div className="app-update__text">
        {update.mandatory ? "Update required" : "Update available"}: v{update.versionName}
        <small>
          Installed v{update.installed.versionName}
          {update.notes ? ` · ${update.notes}` : ""}
        </small>
      </div>
      {!update.mandatory && !busy && (
        <button
          type="button"
          className="app-update__later"
          onClick={() => {
            localStorage.setItem(SNOOZE_KEY, String(update.versionCode));
            setUpdate(null);
          }}
        >
          Later
        </button>
      )}
      <button type="button" className="app-update__go" onClick={start} disabled={busy}>
        {busy ? "Downloading…" : "Update"}
      </button>
    </div>
  );
}
